import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  MenuItem,
  Select,
  Modal,
  InputLabel,
  Container,
  FormControl,
  Paper,
  Typography,
  AppBar,
  Toolbar,
  CircularProgress,
  Box,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";

const EditVendor = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [vendor, setVendor] = useState({
    vendorName: "",
    gstin: "",
    contact: "",
    address: "",
    branchId: 0,
  });
  const [branches, setBranches] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [branchError, setBranchError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setLoadingBarProgress(20);
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token = sessionStorage.getItem("token");

        // Fetch branches
        const branchResponse = await fetch(`${apiUrl}/Branch/GetBranch`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        });

        if (!branchResponse.ok) throw new Error("Failed to fetch branches");
        const branchesData = await branchResponse.json();
        setBranches(branchesData || []);

        // Auto-select branch if only one branch is available
        if (branchesData.length === 1) {
          setVendor((prev) => ({
            ...prev,
            branchId: branchesData[0].branchId,
          }));
        }

        // Fetch vendor
        const vendorId = parseInt(id);
        const vendorResponse = await fetch(
          `${apiUrl}/Inventory/Vendor/GetVendors`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({ vendorId }),
          }
        );

        if (!vendorResponse.ok) throw new Error("Failed to fetch vendor");
        const vendorData = await vendorResponse.json();
        const vendorDetails = vendorData.vendors?.find(
          (v) => v.vendorID === vendorId
        );

        if (!vendorDetails) throw new Error("Vendor not found");

        setVendor((prev) => ({
          ...prev,
          vendorName: vendorDetails.vendorName || "",
          gstin: vendorDetails.gstin || "",
          contact: vendorDetails.contact || "",
          address: vendorDetails.address || "",
          branchId: vendorDetails.branchId || prev.branchId,
        }));

        setLoadingBarProgress(100);
        setError(null);
      } catch (error) {
        console.error("Error loading data:", error);
        setError(error.message || "Failed to load page data");
        setLoadingBarProgress(100);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVendor((prev) => ({
      ...prev,
      [name]: name === "branchId" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const vendorId = parseInt(id);

      const payload = {
        vendorID: vendorId,
        vendorName: vendor.vendorName,
        gstin: vendor.gstin,
        contact: vendor.contact,
        address: vendor.address,
        branchId: vendor.branchId,
      };

      const response = await fetch(
        `${apiUrl}/Inventory/Vendor/InsertUpdateVendor`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.msg === "Vendor updated successfully.") {
         setShowSuccessModal(true);

        } else {
          throw new Error(result.msg || "Update failed");
        }
      } else {
        throw new Error(`API request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating vendor:", error);
      setError(error.message || "An error occurred while updating the vendor");
    } finally {
      setLoadingBarProgress(100);
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

 return (
  <Container >
    <LoadingBar
      progress={loadingBarProgress}
      color="rgb(152, 106, 182)"
      height={5}
    />
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <AppBar position="static" sx={{ backgroundColor: "#0B1F3D",  }}>
        <Toolbar>
          <Typography
            variant="h4"
            component="div"
            sx={{ width: '100%',  fontWeight:'bold'}}
          >
            Edit Vendor (ID: {id})
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} sx={{ padding: 4, marginTop:2 }}>
       
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Vendor Name"
            name="vendorName"
            value={vendor.vendorName}
            onChange={handleInputChange}
            margin="normal"
            required
            error={!!error && !vendor.vendorName}
            helperText={
              !!error && !vendor.vendorName ? "Vendor Name is required" : ""
            }
          />
          <TextField
            fullWidth
            label="GSTIN"
            name="gstin"
            value={vendor.gstin}
            onChange={handleInputChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Contact"
            name="contact"
            value={vendor.contact}
            onChange={handleInputChange}
            margin="normal"
            inputProps={{maxLength:'10'}}
          />
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={vendor.address}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal" error={!!branchError}>
            <InputLabel>Branch</InputLabel>
            <Select
              name="branchId"
              value={vendor.branchId}
              onChange={handleInputChange}
              label="Branch"
              disabled={!!branchError || branches.length <= 1}
              renderValue={(selectedValue) => {
                const selectedBranch = branches.find(
                  (branch) => branch.branchId === selectedValue
                );
                return selectedBranch ? selectedBranch.branchName : "";
              }}
            >
              {branches.length > 0 ? (
                branches.map((branch) => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {branch.branchName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No branches available</MenuItem>
              )}
            </Select>
            {branchError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {branchError}
              </Typography>
            )}
          </FormControl>
          {error && (
            <Typography color="error" sx={{ mt: 2, }}>
              {error}
            </Typography>
          )}
          <Box sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2, mr:2,  width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold'
}}
              disabled={isSubmitting || !!branchError}
            >
              UPDATE
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
              sx={{ mt: 2,   width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold'
}}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>

    <Modal
  open={showSuccessModal}
  onClose={() => setShowSuccessModal(false)}
  aria-labelledby="success-modal-title"
  aria-describedby="success-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
        Vendor Updated ✅
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="success-modal-description" sx={{ mb: 2 }}>
        The Vendor has been updated successfully.
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={() => {
            setShowSuccessModal(false);
            navigate(-1); // go back after closing modal
          }}
          variant="contained"
          color="primary"
        >
          OK
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>

  </Container>
);

};

export default EditVendor;
